'use client';

import { useState } from "react";
import Image from "next/image";
import { leadSchema } from '@/app/validation/leadValidationSchema';
import TermsModal from './TermsModal';

const steps = [
  { label: "Full Name", type: "text", name: "fullName", placeholder: "Enter your full name" },
  { label: "Phone Number", type: "tel", name: "phoneNumber", placeholder: "Enter your phone number", pattern: "[\\d+\\s()\\-]*" },
  { label: "Email Address", type: "email", name: "email", placeholder: "Enter your email address" },
  { label: "Business Name", type: "text", name: "businessName", placeholder: "Enter your business name" },
  { label: "Select Processing Volume", type: "radio", name: "processingVolume", options: [
      "$0", "$1,000 - $10,000", "$10,000 - $20,000", "Over $20,000"
    ]
  },
  { label: "About Your Business", type: "textarea", name: "message", placeholder: "Tell us about your business" },
];

type LeadFormProps = {
  mode?: 'steps' | 'quick';
};

export default function LeadForm({ mode = 'steps' }: LeadFormProps) {
  const [formData, setFormData] = useState<any>({});
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessingNext, setIsProcessingNext] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showModal, setShowModal] = useState<{ type: 'terms' | 'privacy', visible: boolean }>({ type: 'terms', visible: false });
  const [modalContent, setModalContent] = useState('');
  const currentStep = steps[step];

  const handleNext = async () => {
    try {
      await leadSchema.validateAt(currentStep.name, formData);
      setError('');
      if (step < steps.length - 1) {
        setStep((prev) => prev + 1); // Immediately transition
      } else {
        handleSubmit(); // Only show loading here
      }
    } catch (validationError: any) {
      if (validationError.name === 'ValidationError') {
        setError(validationError.message);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setError('');
      setStep((prev) => prev - 1);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!accepted) {
      alert("You must agree to the terms and privacy policy.");
      return;
    }
    try {
      await leadSchema.validateAt('phoneNumber', formData);
    } catch (validationError: any) {
      alert(validationError.message);
      return;
    }
    setIsSubmitting(true);
    try {
      console.log(formData);
      const bodyData = {
        ...formData,
        email: formData.email || 'quick@midapos.com',
        processingVolume: formData.processingVolume || 'Quick Form',
        message: formData.message || 'Submitted via quick form',
        accepted: true,
      };
      const res = await fetch('/api/get-started', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });

      if (res.ok) {
        if (mode === 'quick') {
          setShowSuccess(true);
          setFormData({});
          return;
        }
        setShowSuccess(true);
        setFormData({});
        setStep(0);
      } else {
        const data = await res.json();
        alert(data.error || "Failed to submit lead.");
      }
    } catch (error) {
      console.error("Failed to submit lead", error);
      alert("Failed to submit lead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === 'quick') {
    if (showSuccess) {
      return (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="bg-green-50 border border-green-300 rounded-xl p-8 text-center shadow-md">
            <h2 className="text-3xl font-extrabold text-green-600 mb-4">Thank you!</h2>
            <p className="text-gray-700">We received your info. We'll contact you shortly.</p>
          </div>
        </div>
      );
    }
    return (
      <>
        <form
          className="w-full max-w-2xl mx-auto border-gray-200"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="flex justify-center mb-6 animate-logo-entry">
            <Image
              src="/images/MidaPos-POS-and-Pinpad-Systems-Free-No-Contract-Logo-icon.webp"
              alt="MidaPOS logo"
              width={80}
              height={80}
            />
          </div>
          <h2 className="text-xl font-bold text-center text-gray-800 mb-4">Get Approved in Less than 24 Hours</h2>
          <input type="hidden" name="email" value="quick@midapos.com" />
          <input
            type="text"
            name="fullName"
            placeholder="Full Name"
            onChange={handleChange}
            required
            className="w-full p-4 border rounded-lg mb-3"
          />
          <input
            type="tel"
            name="phoneNumber"
            placeholder="Phone Number"
            onChange={handleChange}
            required
            pattern="\d{10}|\d{3}-\d{3}-\d{4}"
            title="Enter 10 digits (e.g. 4155551234) or 123-456-7890"
            className="w-full p-4 border rounded-lg mb-3"
          />
          <input
            type="text"
            name="businessName"
            placeholder="Business Name"
            onChange={handleChange}
            required
            className="w-full p-4 border rounded-lg mb-3"
          />
          <div className="flex items-start gap-2 text-sm text-gray-700 mt-2 mb-4">
            <input
              type="checkbox"
              id="accept"
              required
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              aria-label="Agree to Terms and Privacy Policy"
              className="mt-1"
            />
            <label htmlFor="accept">
              I agree to the{' '}
              <button
                type="button"
                className="text-blue-600 underline"
                aria-label="Read Terms of Service"
                onClick={async () => {
                  const res = await fetch('/terms');
                  const html = await res.text();
                  const dom = document.createElement('html');
                  dom.innerHTML = html;
                  const section = dom.querySelector('section');
                  setModalContent(section?.innerHTML || 'Failed to load Terms.');
                  setShowModal({ type: 'terms', visible: true });
                }}
              >
                Terms of Service
              </button>
              {' and '}
              <button
                type="button"
                className="text-blue-600 underline"
                aria-label="Read Privacy Policy"
                onClick={async () => {
                  const res = await fetch('/privacy');
                  const html = await res.text();
                  const dom = document.createElement('html');
                  dom.innerHTML = html;
                  const section = dom.querySelector('section');
                  setModalContent(section?.innerHTML || 'Failed to load Privacy.');
                  setShowModal({ type: 'privacy', visible: true });
                }}
              >
                Privacy Policy
              </button>
            </label>
          </div>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg font-semibold bg-[#F07C29] text-white hover:bg-[#e56716]"
          >
            Submit
          </button>
        </form>
        {showModal.visible && (
          <TermsModal
            title={showModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            content={modalContent}
            onClose={() => setShowModal({ ...showModal, visible: false })}
          />
        )}
      </>
    );
  }

  return (
    <form className="w-full max-w-2xl mx-auto   border-gray-200" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
      {showSuccess ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="bg-green-50 border border-green-300 rounded-xl p-8 text-center shadow-md">
            <h2 className="text-3xl font-extrabold text-green-600 mb-4">Thank you!</h2>
            <p className="text-gray-700">We received your info. We'll contact you shortly.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-6 animate-logo-entry">
          <Image
  src="/images/MidaPos-POS-and-Pinpad-Systems-Free-No-Contract-Logo-icon.webp"
  alt="MidaPOS logo"
  width={80}
  height={80}
/>
          </div>
          <div className="flex flex-col items-center justify-center text-center mb-6">
        <h1 className="text-xl font-bold text-gray-800 mb-2">Get Approved Now</h1>
        <p className="text-sm text-blue-500 mt-1">We'll get your business set up in no time.</p>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-12" aria-label="Form progress bar">
  <div
    className="bg-[#F07C29] h-2 rounded-full transition-all duration-400"
    style={{ width: `${((step + 1) / steps.length) * 100}%` }}
  ></div>
  <p className="text-sm text-gray-500 text-left mt-2">Step {step + 1} of {steps.length}</p>
</div>

          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">{currentStep.label}</h2>
          
          <div className="transition-all duration-500 ease-in-out transform">
            {currentStep.type === "text" || currentStep.type === "email" || currentStep.type === "tel" ? (
              <input
                type={currentStep.type}
                name={currentStep.name}
                placeholder={currentStep.placeholder}
                value={formData[currentStep.name] || ''}
                onChange={handleChange}
                aria-label={currentStep.label}
                pattern={currentStep.pattern}
                className="w-full p-4 border rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                autoComplete={currentStep.type === "tel" ? "tel" : currentStep.type === "email" ? "email" : "off"}
              />
            ) : currentStep.type === "textarea" ? (
              <textarea
                name={currentStep.name}
                placeholder={currentStep.placeholder}
                value={formData[currentStep.name] || ''}
                onChange={handleChange}
                aria-label={currentStep.label}
                className="w-full p-4 border rounded-lg mb-3 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
              ></textarea>
            ) : (
            <div className="grid grid-cols-2 gap-4 mb-6" role="radiogroup" aria-label="Processing Volume options">
              {currentStep.options?.map((option: string) => (
                <div
                  key={option}
                  role="radio"
                  aria-checked={formData[currentStep.name] === option}
                  onClick={() =>
                    setFormData((prev: any) => ({ ...prev, [currentStep.name]: option }))
                  }
                  className={`flex items-center justify-center text-center border rounded-lg cursor-pointer py-4 px-2 transition text-sm font-medium
                    ${formData[currentStep.name] === option
                      ? 'bg-[#F07C29]/10 border-[#F07C29] text-[#F07C29] shadow-sm'
                      : 'bg-white border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                >
                  {option}
                </div>
              ))}
            </div>

            )}
          </div>
          {step === steps.length - 1 && (
    <div className="flex items-start gap-2 text-sm text-gray-700 mt-2">
      <input
        type="checkbox"
        id="accept"
        required
        checked={accepted}
        onChange={(e) => setAccepted(e.target.checked)}
        aria-label="Agree to Terms and Privacy Policy"
        className="mt-1"
      />
      <label htmlFor="accept">
        I agree to the{' '}
        <button
          type="button"
          className="text-blue-600 underline"
          aria-label="Read Terms of Service"
          onClick={async () => {
            const res = await fetch('/terms');
            const html = await res.text();
            const dom = document.createElement('html');
            dom.innerHTML = html;
            const section = dom.querySelector('section');
            setModalContent(section?.innerHTML || 'Failed to load Terms content.');
            setShowModal({ type: 'terms', visible: true });
          }}
        >
          Terms of Service
        </button>
        {' and '}
        <button
          type="button"
          className="text-blue-600 underline"
          aria-label="Read Privacy Policy"
          onClick={async () => {
            const res = await fetch('/privacy');
            const html = await res.text();
            const dom = document.createElement('html');
            dom.innerHTML = html;
            const section = dom.querySelector('section');
            setModalContent(section?.innerHTML || 'Failed to load Privacy Policy.');
            setShowModal({ type: 'privacy', visible: true });
          }}
        >
          Privacy Policy
        </button>
      </label>
    </div>
  )}
          {error && <p role="alert" className="text-red-500 mb-4 text-sm">{error}</p>}

          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              aria-label="Back"
              className={`px-6 py-3 rounded-lg ${step === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-500 hover:bg-gray-600 text-white'} transition`}
            >
              Back
            </button>

            <button
              type="submit"
              aria-label={(step === steps.length - 1 ? 'Submit' : 'Next')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ease-in-out ${
                isProcessingNext ? 'bg-[#F07C29] hover:bg-[#e56716]' : 'bg-[#F07C29] hover:bg-[#e56716] text-white'
              }`}
              disabled={isProcessingNext}
            >
               {(step === steps.length - 1 ? 'Submit' : 'Next')}
            </button>
          </div>
        </>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="flex space-x-2">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="w-4 h-4 rounded-full bg-[#F07C29] animate-bounce"
                style={{ animationDelay: `${index * 0.2}s` }}
              ></div>
            ))}
          </div>
        </div>
      )}

      {showModal.visible && (
        <TermsModal
          title={showModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
          content={modalContent}
          onClose={() => setShowModal({ ...showModal, visible: false })}
        />
      )}
    </form>
  );
}